import { IPagedData } from "../components/shared/Tables/PagedTable";
import { FormatType, IEbayOffer, IPartAspect, ListingDuration } from "./EbayOffer";
import { IWarehouseLocation } from "./Location";
import { INote } from "./Note";
import { IPartCategoryMaterial } from "./PartCategoryMaterial";
import { IShippingPolicy } from "./ShippingPolicy";
import { IVehicle } from "./Vehicle";
import { IEbayListingBatchItem } from "./bulkListings/BulkListing";
import { BaseEntity, DefaultResponseWithData } from "./system";

export interface CategoryWithParts {
  category: IPartCategory,
  parts?: IPart[], // Optional - will be loaded on-demand when row is expanded
  isMech: boolean,
  price?: number | undefined,
  note?: string | undefined,
  isManual?: boolean,
  isCore?: boolean,
  accountId?: string | null,
  accountName?: string | null,
  accountType?: number | null,
  coreSearchId?: string | null;
  // Aggregate fields from category summary (when using optimized endpoint)
  internalStock?: number;
  totalSold?: number;
  lastSoldDate?: string | null;
  lastSoldPrice?: number | null;
}

export interface IPartResponse extends DefaultResponseWithData<IPartCategoryResponse> { }
export interface ICategoryPartResponse extends DefaultResponseWithData<IPart[]> { }

export interface IVehicleCategoryTypeFilters {
  matchMake?: boolean;
  matchModel?: boolean;
  matchKType?: boolean;
  matchSeries?: boolean;
  matchMk?: boolean;
  matchBodyStyle?: boolean;
  matchDoors?: boolean;
  matchFuelType?: boolean;
  matchEngineCode?: boolean;
  matchEngineSize?: boolean;
  matchExactYear?: boolean;
  matchYearRange?: boolean;
  yearFrom?: number;
  yearTo?: number;
  partStatuses?: PartStatus[];
}

// Filters for the optimized vehicle/category parts endpoint
export interface IVehicleCategoryPartsFilters extends IVehicleCategoryTypeFilters {
  includeSold?: boolean;
  limitSold?: number;
  bodyFilters?: IVehicleCategoryTypeFilters;
  mechFilters?: IVehicleCategoryTypeFilters;
}

/** Body/mech/year/category-status filters shared by CreateJobs → LeadParts category tables (for query sync + invalidation). */
export interface ILeadPartsTableGlobalFilters {
  bodySearchBy: string[];
  bodyYearFrom?: number;
  bodyYearTo?: number;
  mechSearchBy: string[];
  mechYearFrom?: number;
  mechYearTo?: number;
  categoryStatusSearchBy: string[];
  /** Serialized fingerprint when any of the above changes (e.g. JSON.stringify). */
  searchVersion: string;
}
export interface ISingleCategoryPartResponse extends DefaultResponseWithData<IPart> { }

export interface ICategoryResponse extends DefaultResponseWithData<IPartCategory[]> { }
export interface ISingleCategoryResponse extends DefaultResponseWithData<IPartCategory> { }

export interface IPartCollectionResponse extends DefaultResponseWithData<IPartDataCollection> { }

export interface IStockStatsResponse extends DefaultResponseWithData<IStockStats> { }

export interface ISinglePartAssemblyResponse extends DefaultResponseWithData<IPartAssembly> { }

export interface IPartDataCollection extends IPagedData<IPart> { }

export interface IPartImagesResponse extends DefaultResponseWithData<IPartImage[]> { }
export interface IPartImageMpnAnalysisResponse extends DefaultResponseWithData<IPartImageMpnAnalysis> { }
export interface IProcessMpnImageResponse extends DefaultResponseWithData<IMpnImageAnalysisResult> { }

export interface IPartRefundSentRequest {
  refundDetail: RefundDetail
}

export interface RefundDetail {
  itemizedRefundDetail: ItemizedRefundDetail[]
  totalAmount: TotalAmount
}

export interface ItemizedRefundDetail {
  refundAmount: RefundAmount
  refundFeeType: string
  restockingFeePercentage: string
}

export interface RefundAmount {
  currency: string
  value: number
}

export interface TotalAmount {
  currency: string
  value: number
}


export interface IStockStats {
  inStock: number
  toBeDissasembled: number
  partsWithoutImages: number
  qcIssues: number
  partsWithoutMPN: number
  averagePartPrice: number
  averagePartYear: number
  averageStockAge: number
  averageDaysToSell: number
  partsConversionRate: number
  totalPartPrice?: number
  partsAddedToday?: number
  partsWithoutLocations?: number
  missingParts?: number
}


export interface IPartCategoryResponse {
  body: IPart[];
  mech: IPart[];
}

export enum PartImageRequirementType {
  Label,
  Top,
  Bottom,
  Left,
  Right,
  Front,
  Back,
  Misc,
  "Security Seal",
  MPN,
  Damage,
  Vehicle
}

export interface IPartRequiredImage {
  label: string
  exampleUrl: string
  type: PartImageRequirementType
  deleted: any
  id: string
  created: string
  updated: any
  required: boolean
  canHaveMultiple: boolean
}

export interface IPartImageRequirementGroup {
  name: any
  partCategories: IPartCategory[]
  partRequiredImages: IPartRequiredImage[]
  id: string
  created: string
  updated: string
}

export interface IPartAssemblyResponse extends DefaultResponseWithData<IPartAssembly[]> { }

export interface IPartAssembly extends BaseEntity {
  name: string;
  order: number | null;

  partAssemblyPartCategories: IPartAssemblyPartCategories[] | undefined;
}

export interface IPartAssemblyPartCategories extends BaseEntity {
  order: number | null;

  partAssembly: IPartAssembly | undefined;
  partAssemblyId: string | undefined;
  partCategory: IPartCategory | undefined;
  partCategoryId: string | undefined;
}


export interface IPartAssemblyOnly extends BaseEntity {
  name: string;
}

export interface IPartCategory extends BaseEntity {
  name: string;
  partOnly: boolean;
  mechOnly: boolean;
  postage: number;
  whCost: number;
  whTime: number;
  disCost: number;
  disTime: number;
  pickingTime: number;
  packingTime: number;
  rsName: string;
  pinnacleName: string;
  frontierName: string;
  ucid: string;
  ucidZone: string;
  notes: string | null;
  safetyNote: string | null;
  dismantlingInstructions: string | null;
  storageNote: string | null;

  lookupPartNumber: string | null;
  skuPrefix: string;
  side: number;
  position: number;

  //* Front end only *//
  averagePrice: number;
  averageMargin: number;
  lookupAveragePrice: number | null;
  marketDataAveragePrice: number | null;
  marketDataSold: number | null;
  lastSoldPrice: number | null;
  rsPrice: number | null;
  rsItem: string | null;
  rsData: any | null;
  // ** //

  partMappings: IPartMapping[];
  partCategoryDefaults: IPartCategoryDefault[];
  partAssemblyPartCategories: IPartAssemblyPartCategories[] | undefined;
  partCategoryMaterials?: IPartCategoryMaterial[] | undefined;
  partImageRequirementGroupId: string | null;
  partImageRequirementGroup: IPartImageRequirementGroup | undefined;
}



export interface IUnlistedStatsResponse {
  awaitingListing: number;
  located: number;
  missingPrice: number;
  missingMpn: number;
  notImaged: number;
  unlistedValue: number | null;

  topUnlistedCategories: ITopUnlistedCategoryResponse[];
  topUnlistedVehicles: TopUnlistedVehicleResponse[];
}

export interface ITopUnlistedCategoryResponse {
  categoryName: string | null;
  count: number;
  located: number;
  value: number | null;
}

export interface TopUnlistedVehicleResponse {
  vrm: string;
  make: string;
  model: string;
  year: number | null;
  count: number;
  value: number | null;
}

export interface IListingStatsResponse {
  totalActive: number;
  averageAge: number;
  over30DaysOld: number;
  over90DaysOld: number;
  averagePrice: number;
  totalPrice: number;
  listingsMissing: { [key: string]: number };
  byCategory: IListingStatsByCategory[];
}

export interface IListingStatsByCategory {
  category: IPartCategory;
  totalActive: number;
  over30DaysOld: number;
  averageAge: number;
  averagePrice: number;
  totalPrice: number;
}


export interface IPartImage extends BaseEntity {
  url: string;
  thumbnailUrl: string;
  isThumbnail: boolean;
  useOnEbay: boolean;
  order: number | null;
  partDataId: string | null;
  partImageMpnAnalysisId: string | null;
  imageType: | null;

  isEndSlate: boolean;
  endSlateOrder: number | null;

  deleted: string | null;
}

export interface IPartImageMpnAnalysis extends BaseEntity {
  partImageId: string;
  partDataId: string | null;
  foundMpn: boolean;
  bestMatchMpn: string | null;
  candidateMpns: string | null;
  confidence: number;
  manufacturer: string | null;
  details: string | null;
}

export interface IMpnImageAnalysisResult {
  foundMpn: boolean;
  bestMatchMpn: string | null;
  candidateMpns: string[];
  confidence: number;
  manufacturer: string | null;
  details: string | null;
}

export enum IPartImageType {
  Label,
  Top,
  Bottom,
  Left,
  Right,
  Front,
  Back,
  Misc,
  SecuritySeal,
  MPN,
  Damage,
  Vehicle
}

export interface IPartQcRequest {
  statusMessage: string;
  partStatus: PartStatus;
  partId: string;
}

export interface IPartCancelRequest {
  cancelReason: string;
  partId: string;
}

export interface IPartImagePatchRequest {
  imageId: string;
  isThumbnail?: boolean | null;
  useOnEbay?: boolean | null;
  order?: number | null;
  imageType?: IPartImageType | null;
}

export interface IPartMapping extends BaseEntity {
  // type: MappingType;
  regex: string;
  priority: number;
}

export enum MappingType {
  Generic
}

export interface IPartCategoryDefaultResponse extends DefaultResponseWithData<IPartCategoryDefault> { }

export interface IPartCategoryDefault extends BaseEntity {
  partCategoryId: string | null;
  format: FormatType;
  duration: ListingDuration;
  buyItNowPrice: number | null;
  auctionStartPrice: number | null;
  auctionReservePrice: number | null;
  enableBestOffer: boolean | null;
  autoAcceptPrice: number | null;
  autoAcceptThreshold: number | null;
  fulfillmentPolicyId: string;
  paymentPolicyId: string;
  returnPolicyId: string;
  locationKey: string;
  width: number | null;
  height: number | null;
  length: number | null;
  weight: number | null;
  ebayCategoryId: string | null;
  ebayStoreCategoryId: string | null;

  titleStructure: string | null;
  titleCase: TitleCase;
  categoryAlias: string | null;

  aspectAssignees: IAspectAssignee[];
  categoryCustomAspects: IPartAspect[];
}

export enum TitleCase {
  "No Change",
  Upper,
  Sentence
}


export enum Assignee {
  None,
  Imager,
  Dismantler
}

export interface IAspectAssignee extends BaseEntity {
  partCategoryDefaultId: string | null;
  aspectName: string | null;
  assignee: Assignee
  imageUrl: string | null;
  isImageRequired: boolean;
}

export interface IPart extends BaseEntity {
  taskId: number;
  dataId: string | null;
  promotionId: string | null;
  stockNumber: string | null;
  tagNumber: string | null;
  title: string | null;
  titleConfigured: boolean;
  titleSanitised: string | null;
  titleLink: string | null;
  ebayThumbnail: string | null;
  cdnThumbnail: string | null;
  price: number | null;
  suggestedPrice: number | null;
  enteredPrice: number | null;
  marketDataPrice: number | null;
  marketDataChecked: string | null;
  lastSoldPrice: number | null;
  margin: number | null;
  originalListedDate: string | null;

  ebayItemNumber: string | null;

  ebayTradingApiItemId: string | null; //Old API Item ID
  ebayInventoryApiOfferId: string | null; //New API Item ID

  activePartOfferId: string | null;
  isPublishedOnShopify: boolean;

  vehicleNumber: string | null;
  isVin: boolean;
  isInternal: boolean;
  isDamaged: boolean;
  side: PartSide;
  position: PartPosition;
  grade: string | null;
  partNumber: string | null;
  partNumberAddedByAi?: boolean;
  oePartNumber: string | null;
  supersededPartNumbers: string | null;
  partsLookupRequestId: string | null;
  lastPartLookupChecked: string | null;
  lastIntelligenceLookup: string | null;
  intelligenceAverage: number | null;
  intelligenceMin: number | null;
  intelligenceMax: number | null;
  matchedUcid: string | null;
  exactLincNumber: string | null;
  listedDate: string | null;
  listingProcessing: string | null;
  soldDate: string | null;
  deleted: string | null;
  deletedReason: number | null;
  deletedBy: string | null;
  processedOn: string | null;

  removedOn: string | null;
  removedBy: string | null;
  passedQcOn: string | null;
  passedQcBy: string | null;
  qcReason: string | null;
  qcApprovedComment: string | null;

  imagedOn: string | null;
  imagedBy: string | null;

  pricedOn: string | null;
  pricedBy: string | null;

  scrappedOn: string | null;
  scrappedBy: string | null;

  lastStockChecked: string | null;

  orderId: string | null;
  pickedOn: string | null;
  pickedBy: string | null;
  shippedOn: string | null;
  shippedBy: string | null;
  shippingLabelDocumentId: string | null;
  trackingNumber: string | null;
  length: number | null;
  width: number | null;
  height: number | null;
  weight: number | null;

  fullName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  stateOrProvince: string | null;
  postcode: string | null;


  partStatus: PartStatus;
  statusMessage: string | null;

  images: IPartImage[];
  offers: IEbayOffer[];
  aspects: IPartAspect[];

  categoryId: string | null;
  category: IPartCategory | null;
  categoryAccepted: boolean;

  vehicleId: string | null;
  vehicle: IVehicle | null;

  warehouseLocationId: string | null;
  warehouseLocation: IWarehouseLocation | null;

  shippingPolicyId: string | null;
  shippingPolicy: IShippingPolicy | null;

  locatedBy: string | null;
  locatedOn: string | null;

  notes: INote[];
  internalNotes: INote[];

  isCore: boolean;
  accountId?: string | null;

  //* Frontend only */
  batchItem?: IEbayListingBatchItem | null;
  // ** */
}

export interface IPartMarketplaces extends BaseEntity {
  ebay: boolean;
  partPro: boolean;
  shopify: boolean;

  partData: IPart | null;
}

export enum PartSide {
  None,
  Left,
  Right
}

export enum PartPosition {
  None,
  Front,
  Rear
}

export enum PartStatus {
  "On The Car",
  "Pending Dismantle",
  Dismantled,
  Damaged,
  QC,
  Missing,
  Sold,
  Deleted,
  Returned
}


export enum AutoDapCategory {
  "Spoilers/Wipers" = 10,
  "Body" = 11,
  "Electric/Transmitter/Databox/Sensor" = 12,
  "Air Conditioning/Heating" = 13,
  "Brakes" = 14,
  "Steering Wheel/Axle/Lever/Pedal" = 15,
  "Hydraulic/Traction/Suspension System" = 16,
  "Vehicle Front Axle, Springs" = 17,
  "Vehicle Rear Axle, Springs" = 18,
  "Engine & Fuel System" = 19,
  "Gear Box/Drive Axle/Middle Axle" = 20,
  "Exhaust/Cleaning" = 21,
  "Instruments/Electric Switches" = 22,
  "Sensors/Lock/Alarm" = 23,
  "Wheels/Tires Accessories" = 24,
  "Front Details" = 25,
  "Doors" = 26,
  "Windows/Glasses" = 27,
  "Rear Details" = 28,
  "Seats & Interior" = 29,
  "All" = 0
}
